import type { IApiResouce } from '@generalTypes/sriTypes';
import {
  AsyncKovResourcePicker,
  useLoadSriOptions,
} from '@kathondvla/react-shared-components/src/components';
import type { EditAsideContactsOrCreators } from '@nodeTypeConfig/configTypes';
import { searchApi } from '@store/api/apiConfig';
import type React from 'react';
import { useEffect, useState } from 'react';

import type { PersonHref } from '@generalTypes/apiTypes';

import { PersonOption } from './PersonOption';

const PersonPicker: React.FC<{
  config: EditAsideContactsOrCreators;
  readonly: boolean;
  initialValue?: PersonHref[] | undefined;
  onChange: (value: Array<IApiResouce> | IApiResouce) => void;
}> = ({ config, readonly, initialValue, onChange }) => {
  const sriLoadOptionsFn = useLoadSriOptions({
    sriClient: searchApi,
    href: '/search',
    parameters: config.options.queryParams,
  });

  const [defaultContactsLoaded, setDefaultContactsLoaded] = useState(false);
  const [missingContacts, setMissingContacts] = useState<Set<string>>(new Set());

  const [currentValueResults, setCurrentValueResults] = useState<IApiResouce[]>([]);

  useEffect(() => {
    let isMounted = true;

    async function loadContacts(value: string[]) {
      try {
        const hrefsSet = new Set(value);

        const apiSearch = await searchApi.getRaw('/search', {
          ...config.options.queryParams,
          hrefs: Array.from(hrefsSet).join(','),
        });

        let missingApiSearch: any = null;

        if (apiSearch.results.length !== hrefsSet.size) {
          // sometimes the queryParams of the options filter out some of the already selected contacts.
          // in that case we need to do a new search with the missing hrefs, and label them as mismatched in the UI
          const missingHrefs = Array.from(hrefsSet).filter(
            (href) => !apiSearch.results.some((result) => result.$$meta.permalink === href),
          );

          missingApiSearch = await searchApi.getRaw('/search', {
            expand: 'FULL',
            types: 'person,organisational_unit',
            limit: 30,
            highlight: 'false',
            hrefs: missingHrefs.join(','),
          });

          setMissingContacts(new Set(missingHrefs));
        }

        if (isMounted) {
          if (missingApiSearch) {
            setCurrentValueResults([...apiSearch.results, ...missingApiSearch.results]);
          } else {
            setCurrentValueResults(apiSearch.results);
          }
          setDefaultContactsLoaded(true);
        }
      } catch (error) {
        if (isMounted) {
          console.error(error);
        }
      }
    }
    if (initialValue && initialValue.length > 0 && !defaultContactsLoaded) {
      loadContacts(initialValue);
    } else if (!initialValue || initialValue.length === 0) {
      setDefaultContactsLoaded(true);
    }

    return () => {
      isMounted = false;
    };
  }, [config.options.queryParams, initialValue, defaultContactsLoaded]);

  return (
    <>
      <label className="control-label">{config.label}</label>
      {!defaultContactsLoaded && <div className="form-group shimmer narrowLine" />}
      {defaultContactsLoaded && (
        <AsyncKovResourcePicker
          defaultValue={currentValueResults}
          onChange={onChange}
          loadOptions={sriLoadOptionsFn}
          disabled={readonly}
          optionValue={(option) => option.key}
          placeholder={`Zoek ${config.label.toLowerCase()}`}
          clearable={false}
          multiple={'multiple' in config.options ? config.options.multiple : true}
          optionTemplate={(option) => (
            <PersonOption
              option={option}
              isMismatch={missingContacts.has(option.$$meta.permalink)}
            />
          )}
        />
      )}
    </>
  );
};

export default PersonPicker;
